
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// types
import { InvoiceNewServiceT, FullOrderT } from '@/core/types/orders.types'
import {
    AdditionalServicesTypeT,
    UpdateAdditionalServicesPayloadT,
} from '@/core/types/additional_services.types'

// realisations
import { orders } from '@/core/realisations/orders.realisation'
import { additionalServices } from '@/core/realisations/additional_services.realisation'

// notifications
import useNotifications from '@/common/composable/useNotifications'

// validations
import useVuelidate from '@vuelidate/core'
import { integer } from '@vuelidate/validators'

// classes
import { DataState } from '@/core/classes/DataState.class'

export default defineComponent({
    components: {
        PageTitle,
        Loader,
        TheButton,
        TheInput,
        Checkbox,
    },
    setup() {
        const route = useRoute()
        const router = useRouter()

        const notifications = useNotifications()

        const order_id = Number(route.params.order_id as string)

        const state = reactive({
            is_loading: false,
            is_failed: false,
            order: null as FullOrderT | null,
        })

        const data_state = reactive({
            additional_services: new DataState<AdditionalServicesTypeT>({}),
        })

        const additional_services_state = reactive({
            is_loading: false,
            //
            loadersCount: 0,
            manipulatorCount: 0,
            craneCount: 0,
            carCount: 0,
            hydraulicTrolleyCount: 0,
        })

        const additional_services_state_form$ = useVuelidate(
            {
                loadersCount: { integer },
                manipulatorCount: { integer },
                craneCount: { integer },
                carCount: { integer },
                hydraulicTrolleyCount: { integer },
            },
            additional_services_state
        )

        const loadAdditionalServices = async () => {
            data_state.additional_services.pagination.current_page = 1

            try {
                data_state.additional_services.is_loading = true

                const response = await additionalServices.getList()
                data_state.additional_services.list = response.data

                data_state.additional_services.pagination.total_pages = 1

                data_state.additional_services.is_failed = false
                data_state.additional_services.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.additional_services.failure.catchError(error)

                if (!data_state.additional_services.failure.error_state.is_cancel) {
                    data_state.additional_services.is_failed = true
                }
            } finally {
                if (!data_state.additional_services.failure.error_state.is_cancel) {
                    data_state.additional_services.is_loading = false
                }
            }
        }

        const getAdditionalService = (type_code: string): InvoiceNewServiceT | null => {
            const service = (state.order as FullOrderT).order_additional_services_values.find(
                (service) => service.type_code === type_code
            )
            if (!service) return null

            return service
        }

        const initializeNewAdditionalServices = () => {
            const loaders_service = getAdditionalService('loader')
            const manipulator_service = getAdditionalService('manipulator')
            const crane_service = getAdditionalService('crane')
            const car_service = getAdditionalService('car')
            const hydraulic_trolley_service = getAdditionalService('hydraulic_trolley')

            if (loaders_service) {
                additional_services_state.loadersCount = loaders_service.value || 1
            }
            if (manipulator_service) {
                additional_services_state.manipulatorCount = manipulator_service.value || 1
            }
            if (crane_service) {
                additional_services_state.craneCount = crane_service.value || 1
            }
            if (car_service) {
                additional_services_state.carCount = car_service.value || 1
            }
            if (hydraulic_trolley_service) {
                additional_services_state.hydraulicTrolleyCount =
                    hydraulic_trolley_service.value || 1
            }
        }

        const loadOrder = async () => {
            try {
                state.is_loading = true

                const response = await orders.getOrder(order_id)
                state.order = response.data

                if (state.order.order_additional_services_values.length > 0) {
                    initializeNewAdditionalServices()
                }

                state.is_failed = false
            } catch (error) {
                console.error(error)
                state.is_failed = true

                notifications.notifyError({ error, title: 'При получении заказа произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadOrder()
            loadAdditionalServices()
        })

        const getAdditionalServiceTypeId = (code: string): number | null => {
            const service = data_state.additional_services.list.find(
                (additional_service) => additional_service.code === code
            )
            if (!service) return null

            return service.id
        }

        const getPayload = (): UpdateAdditionalServicesPayloadT => {
            const additional_service_values = [] as {
                type_id: number
                value: number
                duration: number
            }[]

            if (additional_services_state.carCount > 0) {
                const car_type_id = getAdditionalServiceTypeId('car')

                if (car_type_id) {
                    additional_service_values.push({
                        type_id: car_type_id,
                        value: Number(additional_services_state.carCount),
                        duration: 1,
                    })
                }
            }

            if (additional_services_state.craneCount > 0) {
                const crane_type_id = getAdditionalServiceTypeId('crane')

                if (crane_type_id) {
                    additional_service_values.push({
                        type_id: crane_type_id,
                        value: Number(additional_services_state.craneCount),
                        duration: 1,
                    })
                }
            }

            if (additional_services_state.hydraulicTrolleyCount > 0) {
                const hydraulic_trolley_type_id = getAdditionalServiceTypeId('hydraulic_trolley')

                if (hydraulic_trolley_type_id) {
                    additional_service_values.push({
                        type_id: hydraulic_trolley_type_id,
                        value: Number(additional_services_state.hydraulicTrolleyCount),
                        duration: 1,
                    })
                }
            }

            if (additional_services_state.loadersCount > 0) {
                const loader_type_id = getAdditionalServiceTypeId('loader')

                if (loader_type_id) {
                    additional_service_values.push({
                        type_id: loader_type_id,
                        value: Number(additional_services_state.loadersCount),
                        duration: 1,
                    })
                }
            }

            if (additional_services_state.manipulatorCount > 0) {
                const manipulator_type_id = getAdditionalServiceTypeId('manipulator')

                if (manipulator_type_id) {
                    additional_service_values.push({
                        type_id: manipulator_type_id,
                        value: Number(additional_services_state.manipulatorCount),
                        duration: 1,
                    })
                }
            }

            return {
                additional_service_values,
            }
        }

        const updateAdditionalServices = async () => {
            if (additional_services_state.is_loading) return

            additional_services_state_form$.value.$touch()
            if (additional_services_state_form$.value.$invalid) return

            try {
                additional_services_state.is_loading = true

                const payload = getPayload()
                await additionalServices.updateOrderAdditionalServices(order_id, payload)

                notifications.notify({ title: 'Доп. услуги сохранены', type: 'success' })
                router.push(`/order/${order_id}`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении доп. услуг произошла ошибка',
                })
            } finally {
                additional_services_state.is_loading = false
            }
        }

        const pageTitle = computed(() => {
            if (!state.order) return 'Редактирование доп. услуг заказа'
            return `Редактирование доп. услуг накладной ${order_id}`
        })

        return {
            state,
            data_state,
            additional_services_state,
            additional_services_state_form$,
            pageTitle,
            loadOrder,
            updateAdditionalServices,
        }
    },
})
